import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { useShopPreferences } from '@/hooks/useShopPreferences'
import { platformSupportService } from '@/services/platformSupportService'
import type { SupportThread } from '@/services/supportChatService'
import { SupportConversationPanel } from '@/components/support/SupportConversationPanel'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from '@/i18n/useTranslation'

const POSITION_KEY = 'platform-support-fab-pos'
const FAB_SIZE = 48
const EDGE_PAD = 12
const DRAG_THRESHOLD_PX = 6

type FabPos = { x: number; y: number }

function defaultPos(vw = 1280, vh = 800): FabPos {
  return {
    x: Math.max(EDGE_PAD, vw - FAB_SIZE - 20),
    y: Math.max(EDGE_PAD, vh - FAB_SIZE - 20),
  }
}

function clampPos(pos: FabPos, vw: number, vh: number): FabPos {
  const maxX = Math.max(EDGE_PAD, vw - FAB_SIZE - EDGE_PAD)
  const maxY = Math.max(EDGE_PAD, vh - FAB_SIZE - EDGE_PAD)
  return {
    x: Math.min(maxX, Math.max(EDGE_PAD, pos.x)),
    y: Math.min(maxY, Math.max(EDGE_PAD, pos.y)),
  }
}

function loadPos(vw: number, vh: number): FabPos {
  try {
    const raw = localStorage.getItem(POSITION_KEY)
    if (!raw) return defaultPos(vw, vh)
    const parsed = JSON.parse(raw) as FabPos
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return defaultPos(vw, vh)
    return clampPos(parsed, vw, vh)
  } catch {
    return defaultPos(vw, vh)
  }
}

/**
 * Floating help for tenant staff → messages go to the platform shop Support Chat.
 * Compact draggable FAB so it can be moved off pagination / page controls.
 */
export function PlatformSupportWidget() {
  const { t } = useTranslation('common')
  const user = useAuthStore((s) => s.user)
  const { shop } = useShopPreferences()
  const isTenantStaff = Boolean(user && !user.is_web_user && user.shop_id)
  const isPlatformShop = shop?.is_platform_shop === true

  const [open, setOpen] = useState(false)
  const [threads, setThreads] = useState<SupportThread[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [composing, setComposing] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [unread, setUnread] = useState(0)
  const [unavailable, setUnavailable] = useState(false)
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }))
  const [pos, setPos] = useState<FabPos>(() =>
    loadPos(
      typeof window !== 'undefined' ? window.innerWidth : 1280,
      typeof window !== 'undefined' ? window.innerHeight : 800,
    ),
  )
  const [dragging, setDragging] = useState(false)

  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
    moved: boolean
  } | null>(null)

  const selected = useMemo(
    () => threads.find((thread) => thread.thread_id === selectedId) || null,
    [threads, selectedId],
  )

  const transport = useMemo(
    () => ({
      listMessages: platformSupportService.listMessages,
      postMessage: (
        threadId: number,
        payload: { body: string; is_internal?: boolean; files?: File[] },
      ) =>
        platformSupportService.postMessage(threadId, {
          body: payload.body,
          files: payload.files,
        }),
      markRead: platformSupportService.markRead,
      openEventStream: (opts: {
        threadId?: number
        onEvent: Parameters<typeof platformSupportService.openEventStream>[0]['onEvent']
      }) => platformSupportService.openEventStream(opts),
    }),
    [],
  )

  const refreshUnread = useCallback(async () => {
    if (!isTenantStaff || isPlatformShop) return
    try {
      const res = await platformSupportService.getUnread()
      if (res.success && res.data) setUnread(res.data.unread_threads)
    } catch {
      /* optional badge */
    }
  }, [isTenantStaff, isPlatformShop])

  const load = useCallback(async () => {
    if (!isTenantStaff || isPlatformShop) return
    setLoading(true)
    setUnavailable(false)
    try {
      const [listRes, unreadRes] = await Promise.all([
        platformSupportService.listThreads({ status: 'all', limit: 20, kind: 'chat' }),
        platformSupportService.getUnread(),
      ])
      if (listRes.success && listRes.data) {
        setThreads(listRes.data.threads)
        setSelectedId((prev) => prev ?? listRes.data!.threads[0]?.thread_id ?? null)
      }
      if (unreadRes.success && unreadRes.data) {
        setUnread(unreadRes.data.unread_threads)
      }
    } catch {
      setUnavailable(true)
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [isTenantStaff, isPlatformShop])

  // Unread badge on the closed bubble (does not create a platform contact).
  useEffect(() => {
    if (!isTenantStaff || isPlatformShop) return
    void refreshUnread()
    const id = window.setInterval(() => void refreshUnread(), 30_000)
    return () => window.clearInterval(id)
  }, [isTenantStaff, isPlatformShop, refreshUnread])

  useEffect(() => {
    if (!isTenantStaff || isPlatformShop || open) return
    void refreshUnread()
  }, [isTenantStaff, isPlatformShop, open, refreshUnread])

  // Thread list only after the user opens the panel (avoids contact INSERT on every page).
  useEffect(() => {
    if (!isTenantStaff || isPlatformShop || !open) return
    void load()
  }, [isTenantStaff, isPlatformShop, open, load])

  useEffect(() => {
    const onResize = () => {
      const next = { w: window.innerWidth, h: window.innerHeight }
      setViewport(next)
      setPos((p) => clampPos(p, next.w, next.h))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!isTenantStaff || isPlatformShop) return null

  const startThread = async () => {
    if (!body.trim()) return
    setSending(true)
    try {
      const res = await platformSupportService.createThread({
        subject: subject.trim() || undefined,
        body: body.trim(),
      })
      if (res.success && res.data) {
        setComposing(false)
        setSubject('')
        setBody('')
        await load()
        setSelectedId(res.data.thread.thread_id)
      }
    } finally {
      setSending(false)
    }
  }

  const onFabPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
    }
    setDragging(true)
  }

  const onFabPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.moved && dx * dx + dy * dy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      drag.moved = true
    }
    if (!drag.moved) return
    setPos(clampPos({ x: drag.originX + dx, y: drag.originY + dy }, viewport.w, viewport.h))
  }

  const endDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragRef.current = null
    setDragging(false)
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    if (drag.moved) {
      setPos((p) => {
        const next = clampPos(p, viewport.w, viewport.h)
        try {
          localStorage.setItem(POSITION_KEY, JSON.stringify(next))
        } catch {
          /* ignore */
        }
        return next
      })
      return
    }
    setOpen((v) => !v)
  }

  const panelWidth = Math.min(380, viewport.w - 16)
  const panelHeight = Math.min(560, viewport.h * 0.7)
  const panelLeft = Math.min(
    Math.max(EDGE_PAD, pos.x + FAB_SIZE - panelWidth),
    Math.max(EDGE_PAD, viewport.w - panelWidth - EDGE_PAD),
  )
  const spaceAbove = pos.y - EDGE_PAD
  const spaceBelow = viewport.h - (pos.y + FAB_SIZE) - EDGE_PAD
  const openAbove = spaceAbove >= panelHeight + 8 || spaceAbove > spaceBelow
  const panelTop = openAbove
    ? Math.max(EDGE_PAD, pos.y - panelHeight - 8)
    : Math.min(pos.y + FAB_SIZE + 8, Math.max(EDGE_PAD, viewport.h - panelHeight - EDGE_PAD))

  return (
    <>
      {open && (
        <div
          className="fixed z-40 flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          style={{ left: panelLeft, top: panelTop, width: panelWidth, height: panelHeight }}
        >
          <header className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
            <div>
              <div className="text-sm font-semibold">{t('platformSupport.title')}</div>
              <div className="text-xs text-slate-300">{t('platformSupport.subtitle')}</div>
              <Link
                to="/help/contact"
                onClick={() => setOpen(false)}
                className="mt-1 block text-[11px] text-slate-300 underline hover:text-white"
              >
                {t('platformSupport.openTicketPage')}
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 hover:bg-white/10"
              aria-label={t('actions.close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </header>

          {unavailable ? (
            <div className="flex flex-1 items-center justify-center px-4 text-center text-sm text-gray-500">
              {t('platformSupport.unavailable')}
            </div>
          ) : composing ? (
            <div className="flex flex-1 flex-col gap-3 p-4">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('platformSupport.subjectPlaceholder')}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder={t('platformSupport.bodyPlaceholder')}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setComposing(false)}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm"
                >
                  {t('actions.back')}
                </button>
                <button
                  type="button"
                  disabled={sending || !body.trim()}
                  onClick={() => void startThread()}
                  className="flex-1 rounded-lg bg-slate-900 py-2 text-sm text-white disabled:opacity-50"
                >
                  {sending ? t('platformSupport.sending') : t('platformSupport.send')}
                </button>
              </div>
            </div>
          ) : selected ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  {t('platformSupport.allChats')}
                </button>
                <button
                  type="button"
                  onClick={() => setComposing(true)}
                  className="text-xs font-medium text-slate-900 hover:underline"
                >
                  {t('platformSupport.new')}
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <SupportConversationPanel
                  thread={selected}
                  mode="customer"
                  transport={transport}
                  skipGlobalUnreadBadge
                  onMarkedRead={() => setUnread((n) => Math.max(0, n - 1))}
                  onThreadUpdated={(updated) => {
                    setThreads((prev) =>
                      prev.map((thread) =>
                        thread.thread_id === updated.thread_id ? { ...thread, ...updated } : thread,
                      ),
                    )
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex justify-end border-b border-gray-100 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setComposing(true)}
                  className="text-xs font-medium text-slate-900 hover:underline"
                >
                  {t('platformSupport.newConversation')}
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-10">
                    <LoadingSpinner />
                  </div>
                ) : threads.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    {t('platformSupport.empty')}
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setComposing(true)}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white"
                      >
                        {t('platformSupport.askQuestion')}
                      </button>
                    </div>
                  </div>
                ) : (
                  threads.map((thread) => (
                    <button
                      key={thread.thread_id}
                      type="button"
                      onClick={() => setSelectedId(thread.thread_id)}
                      className="w-full border-b border-gray-50 px-3 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="truncate text-sm font-medium text-gray-900">
                        {thread.subject ||
                          thread.last_message_preview ||
                          t('platformSupport.conversation')}
                      </div>
                      <div className="text-[11px] capitalize text-gray-400">{thread.status}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        title={`${t('platformSupport.needHelp')} — ${t('platformSupport.dragHint')}`}
        aria-label={open ? t('actions.close') : t('platformSupport.needHelp')}
        className={`fixed z-50 flex h-12 w-12 touch-none items-center justify-center rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ left: pos.x, top: pos.y }}
      >
        {open ? (
          <XMarkIcon className="h-5 w-5" />
        ) : (
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
        )}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </>
  )
}
